import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import CRMLeadDetail from "../../pages/CRMLeadDetail";
import { CrmDetailDrawer } from "./CrmDetailDrawer";

/**
 * GlobalLeadDrawer —— 全局「客户线索详情」抽屉（挂一次，全站可用）
 *
 * 需求来源（2026-10-13）：客户线索详情已改成抽屉，但从「转介绍 / 通话记录 / 客户公海 / 通知」
 * 等页面点客户时，站内 20+ 处链接写的是 `/crm/leads/:id` —— 一旦命中该路由，页面就变成
 * 「客户线索 CRM 的列表 + 抽屉」，来源页与左侧菜单高亮都被切走。用户要求：既是抽屉，
 * 就该就地弹出，别跳菜单。
 *
 * 做法（关键取舍：为什么在路由层统一拦截，而不是逐个改那 20+ 处链接）：
 *   ① 逐个改链接改动面大、易漏，且以后新增入口还会再犯；
 *   ② 这里集中拦截：把「从别的页面进入 /crm/leads/:id」改写成「来源页 + ?lead=<id>」，
 *      抽屉就地弹出 —— 来源页、面包屑、菜单高亮全部保持不动；
 *   ③ 若来源页本来就是 `/crm/leads`（在客户看板里点行），或用户是直链 / 新标签打开，
 *      则保持 `/crm/leads/:id` 原样，仍渲染「列表 + 抽屉」，深链照常可用。
 *
 * ⚠️ 拦截点为什么是 `history.pushState / replaceState`，而不是「先跳详情路由、再在 effect 里改回来」：
 *   后者会让**背景页先卸载、再重新挂载**。除了白白重新拉一遍数据，还会触发背景页自己的 URL
 *   同步 effect —— 例如 `CRMPool.jsx` 会用「只含筛选键的新 URLSearchParams」整体替换 search
 *   （`setSp(n, { replace: true })`），把刚写进去的 `lead` 参数直接冲掉。
 *   实测（劫持 history 抓到的调用序列）：
 *     pushState    -> /crm/leads/LEADxxx
 *     replaceState -> /crm/pool?lead=LEADxxx   ← 本组件写的
 *     replaceState -> /crm/pool                ← 被 CRMPool 重挂时的 effect 冲掉，抽屉随之消失
 *   改成「导航前改写」后，背景页根本不会卸载，也就不会重跑那些 effect。
 *
 * URL 契约：
 *   · `?lead=<id>` —— 抽屉的查询参数形态，可挂在任意路由上（不改路由、不动菜单）；
 *   · `/crm/leads/<id>` —— 路径形态，保留给直链 / 分享 / 站内既有链接（从客户看板进入时仍是它）。
 *   两种形态由本组件统一渲染抽屉，是全站唯一的挂载点。
 */

// 详情在 URL 里的承载参数（查询参数形态）
export const LEAD_PARAM = "lead";
// 路径形态：/crm/leads/<id>（可带 query / hash）
const DETAIL_URL = /^\/crm\/leads\/([^/?#]+)/;
// 当前路径等于它时，说明用户本来就在客户线索列表里 → 保持路径形态
const LEADS_LIST_PATH = "/crm/leads";
// 「本身就是一个详情抽屉」的路由。命中说明底下已经有一层抽屉（如已签约客户详情、
// 设计项目详情），本抽屉就要作为**第 2 层**叠在上面（更窄 70px + z 更高，
// 见 CrmDetailDrawer 的 level 参数）。
// ⚠️ 以后新增别的详情抽屉路由，记得加进这里，否则会出现「新抽屉被压在下面看不见」。
// 注：项目详情抽屉（?project=<id>，见 components/GlobalProjectDrawer.jsx）不在此列 ——
// 两者同时打开时约定项目恒在上，详见下方 level 判定处的说明。
// 设计项目详情直接占路由路径（/design/projects/:pid、/design/presign/:pid），不是 ?project=，
// 所以也必须识别为底层抽屉。否则从设计项目「CRM 资料 → 打开线索」会错误地按 level 0 打开，
// 与设计项目抽屉同层甚至被其覆盖，表现为点击后看不到正确的客户线索。
const STACKED_BASE_PATHS = [
  /^\/crm\/signed\/[^/]+$/,
  /^\/design\/(?:projects|presign)\/[^/]+$/,
];

// 「抽屉参数在 URL 里的出现顺序 = 它们的叠放顺序」。
// 依据：导航改写时新参数一律**追加**到来源页 query 末尾（见下方 rewrite 里的 URLSearchParams.set），
// 所以后打开的抽屉参数排在后面 → 用出现顺序算 level，天然满足「谁后开谁在上」，
// 且刷新 / 分享链接后顺序不变。
//   · ?project=X&lead=Y（项目先开、再点线索）→ 线索在后 → 线索在上
//   · ?lead=Y&project=X（线索先开、再点项目）→ 项目在后 → 项目在上
// 两个方向都对，不再需要「谁恒在上」的硬约定。
export const drawerStackOrder = (search) => {
  const order = [];
  const re = /[?&](lead|project)=/g;
  let m;
  while ((m = re.exec(search))) if (!order.includes(m[1])) order.push(m[1]);
  return order;
};

export const GlobalLeadDrawer = () => {
  const { lang } = useApp();
  const zh = lang === "zh";
  const location = useLocation();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  // 「上一个非详情页面」—— 仅作为兜底路径使用（主路径已被 history 拦截处理）
  const bgRef = useRef(null);

  // ── 主拦截：在导航真正发生之前改写 URL，背景页不卸载 ─────────────────────────
  // React Router 的所有跳转最终都会调用 window.history.pushState / replaceState，
  // 在这里包一层，等于拿到一个「全局导航钩子」（React Router 官方并未提供该钩子）。
  // 拦截范围严格限定：只改「形如 /crm/leads/<id> 且当前页不是客户线索列表」的目标；
  // 其余导航原样透传，不影响任何其它行为；组件卸载时还原原始方法。
  useEffect(() => {
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    const rewrite = (url) => {
      if (typeof url !== "string") return url;                        // 不传 url = 只改 state，不动
      const m = DETAIL_URL.exec(url);
      if (!m) return url;                                             // 不是线索详情路由，不动
      if (window.location.pathname === LEADS_LIST_PATH) return url;   // 就在线索列表里 → 保持路径形态
      const next = new URLSearchParams(window.location.search);
      next.set(LEAD_PARAM, m[1]);                                     // 保留来源页原有参数，追加 lead
      return `${window.location.pathname}?${next.toString()}`;
    };
    window.history.pushState = function (state, title, url) { return origPush.call(this, state, title, rewrite(url)); };
    window.history.replaceState = function (state, title, url) { return origReplace.call(this, state, title, rewrite(url)); };
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  // ── 兜底：万一某次导航绕过了上面的拦截，仍能把 URL 纠正回来 ───────────────────
  // 代价是背景页会重挂一次，所以只作保险，不作主路径。
  const pathMatch = DETAIL_URL.exec(location.pathname);
  const pathLeadId = pathMatch ? pathMatch[1] : "";
  const queryLeadId = sp.get(LEAD_PARAM) || "";
  const leadId = queryLeadId || pathLeadId;

  useLayoutEffect(() => {
    if (!pathMatch) {
      bgRef.current = { pathname: location.pathname, search: location.search };
      return;
    }
    const bg = bgRef.current;
    if (!bg || bg.pathname === LEADS_LIST_PATH) return;
    const n = new URLSearchParams(bg.search);
    n.set(LEAD_PARAM, pathLeadId);
    nav(`${bg.pathname}?${n.toString()}`, { replace: true });
  }, [location.pathname, location.search, pathMatch, pathLeadId, nav]);

  // 关闭抽屉：
  //   · 查询参数形态 → 只摘掉 lead（replace，不留历史垃圾），来源页原样停留；
  //   · 路径形态（直链 / 来源就是线索列表）→ 优先回上一页，直链才兜底回线索看板。
  const close = () => {
    if (queryLeadId) {
      const n = new URLSearchParams(sp);
      n.delete(LEAD_PARAM);
      const s = n.toString();
      nav(`${location.pathname}${s ? `?${s}` : ""}`, { replace: true });
      return;
    }
    const idx = typeof window !== "undefined" && window.history.state ? window.history.state.idx : undefined;
    if (typeof idx === "number" && idx > 0) { nav(-1); return; }
    nav("/crm/leads?tab=board");
  };

  if (!leadId) return null;
  // 抽屉层级 = 页面自带抽屉（STACKED_BASE_PATHS 命中，占第 0 层）+ 本参数在 URL 里的出现顺序。
  // 参数顺序即打开顺序 → 后打开的叠在上面（详见 drawerStackOrder 的说明）。
  const level = (STACKED_BASE_PATHS.some((re) => re.test(location.pathname)) ? 1 : 0)
    + Math.max(0, drawerStackOrder(location.search).indexOf(LEAD_PARAM));
  return (
    <CrmDetailDrawer title={zh ? "客户线索详情" : "Lead detail"} onClose={close} testId="lead-detail-drawer" level={level}>
      <CRMLeadDetail embedded leadId={leadId} />
    </CrmDetailDrawer>
  );
};

export default GlobalLeadDrawer;
